import React, { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import {
  Cpu, Plus, Loader2, Copy, Check, RefreshCw, ShieldCheck, Radio, KeyRound, Tag,
  Activity, AlertTriangle, X, Store,
} from 'lucide-react';
import {
  Device, Rollup, OwnershipMode, Sensitivity, IdentityLevel,
  listMyDevices, provisionDevice, rotateDeviceKey, getDeviceRollups, createDataProduct,
} from '../services/iotService';

const IOT_URL = (import.meta as any).env.VITE_IOT_WORKER_URL || 'https://tinaney-iot.<account>.workers.dev';
const COLORS = ['#6366F1', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

const tooltipStyle = { backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#fff', fontSize: 12 } as const;
const sevColor: Record<Sensitivity, string> = { LOW: 'bg-emerald-500/15 text-emerald-300', MEDIUM: 'bg-amber-500/15 text-amber-300', HIGH: 'bg-red-500/15 text-red-300' };

const DevicesView: React.FC = () => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [reveal, setReveal] = useState<{ id: string; key: string; label: string } | null>(null);
  const [selected, setSelected] = useState<Device | null>(null);

  const load = async () => { setLoading(true); try { setDevices(await listMyDevices()); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-in fade-in duration-300">
      <div className="flex items-center justify-between border-b border-slate-700 pb-4">
        <h1 className="text-xl font-bold text-white flex items-center gap-2"><Cpu className="text-indigo-400" size={22} /> My IoT Devices</h1>
        <button onClick={() => setShowForm((s) => !s)} className="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2"><Plus size={16} /> Register device</button>
      </div>

      {showForm && <RegisterForm onDone={(r) => { setShowForm(false); setReveal(r); load(); }} />}

      {reveal && <KeyReveal reveal={reveal} onClose={() => setReveal(null)} />}

      {loading ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-indigo-500" /></div> : devices.length === 0 ? (
        <div className="text-center py-12 bg-slate-800/50 rounded-xl border border-dashed border-slate-700">
          <Cpu size={40} className="mx-auto text-slate-600 mb-3" />
          <p className="text-slate-400">No devices yet. Register one to start streaming data.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {devices.map((d) => (
            <button key={d.id} onClick={() => setSelected(d)} className="text-left bg-slate-800 border border-slate-700 hover:border-indigo-500/50 rounded-xl p-4 transition-colors">
              <div className="flex items-center justify-between">
                <span className="text-white font-bold truncate">{d.label || d.device_type}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${d.status === 'ACTIVE' ? 'bg-emerald-500/15 text-emerald-300' : 'bg-slate-600 text-slate-300'}`}>{d.status}</span>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2 text-[10px]">
                <span className={`px-1.5 py-0.5 rounded ${d.ownership_mode === 'TINANEY' ? 'bg-indigo-500/15 text-indigo-300' : 'bg-slate-700 text-slate-300'}`}>{d.ownership_mode === 'TINANEY' ? 'Tinaney-owned' : 'You own'}</span>
                <span className={`px-1.5 py-0.5 rounded ${sevColor[d.sensitivity]}`}>{d.sensitivity}</span>
                <span className="px-1.5 py-0.5 rounded bg-slate-700 text-slate-300 flex items-center gap-1">{d.identity_level === 'SIGNED_DEVICE' ? <><ShieldCheck size={10} /> Verified</> : <><Radio size={10} /> Gateway</>}</span>
              </div>
              <p className="text-xs text-slate-500 mt-2">{d.last_seen ? `Last seen ${new Date(d.last_seen).toLocaleString()}` : 'No data yet'}</p>
            </button>
          ))}
        </div>
      )}

      {selected && <DeviceDetail device={selected} onClose={() => setSelected(null)} onRotated={(k) => setReveal({ id: selected.id, key: k, label: selected.label || selected.device_type })} />}
    </div>
  );
};

// ── Register form ────────────────────────────────────────────────────────────
const RegisterForm: React.FC<{ onDone: (r: { id: string; key: string; label: string }) => void }> = ({ onDone }) => {
  const [label, setLabel] = useState('');
  const [deviceType, setDeviceType] = useState('sensor');
  const [ownership, setOwnership] = useState<OwnershipMode>('USER');
  const [sensitivity, setSensitivity] = useState<Sensitivity>('LOW');
  const [identity, setIdentity] = useState<IdentityLevel>('GATEWAY');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!label.trim()) return alert('Give the device a label.');
    setBusy(true);
    try {
      const r = await provisionDevice({ label, device_type: deviceType, ownership_mode: ownership, sensitivity, identity_level: identity });
      onDone({ id: r.device_id, key: r.device_key, label });
    } catch (e: any) { alert(e.message || 'Could not register device.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
      <Field label="Label"><input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Rooftop air sensor" className="inp" /></Field>
      <Field label="Type"><input value={deviceType} onChange={(e) => setDeviceType(e.target.value)} placeholder="sensor" className="inp" /></Field>
      <Field label="Ownership"><select value={ownership} onChange={(e) => setOwnership(e.target.value as OwnershipMode)} className="inp"><option value="USER">You own the device & data</option><option value="TINANEY">Tinaney-owned (you host, earn yield)</option></select></Field>
      <Field label="Data sensitivity"><select value={sensitivity} onChange={(e) => setSensitivity(e.target.value as Sensitivity)} className="inp"><option value="LOW">Low (sellable raw)</option><option value="MEDIUM">Medium</option><option value="HIGH">High (aggregate/anonymize)</option></select></Field>
      <Field label="Identity"><select value={identity} onChange={(e) => setIdentity(e.target.value as IdentityLevel)} className="inp"><option value="GATEWAY">Gateway (authenticated)</option><option value="SIGNED_DEVICE">Signed device (verified)</option></select></Field>
      <div className="flex items-end"><button onClick={submit} disabled={busy} className="w-full bg-indigo-600 hover:bg-indigo-500 text-white py-2.5 rounded-lg font-bold flex items-center justify-center gap-2 disabled:opacity-50">{busy ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />} Create & get key</button></div>
      <style>{`.inp{width:100%;background:#0f172a;border:1px solid #475569;border-radius:8px;padding:8px 12px;color:#fff;font-size:14px;outline:none}.inp:focus{border-color:#6366f1}`}</style>
    </div>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div><label className="block text-xs text-slate-400 mb-1">{label}</label>{children}</div>
);

// ── One-time key reveal + ingest example ─────────────────────────────────────
const KeyReveal: React.FC<{ reveal: { id: string; key: string; label: string }; onClose: () => void }> = ({ reveal, onClose }) => {
  const [copied, setCopied] = useState<'id' | 'key' | null>(null);
  const copy = (t: string, w: 'id' | 'key') => { navigator.clipboard?.writeText(t); setCopied(w); setTimeout(() => setCopied(null), 1500); };
  const curl = `curl -X POST ${IOT_URL}/ingest \\
 -H 'content-type: application/json' \\
 -d '{"device_id":"${reveal.id}","key":"${reveal.key}","readings":[{"metric":"temp","value":24.5}]}'`;
  return (
    <div className="bg-amber-900/15 border border-amber-500/40 rounded-xl p-5 space-y-3">
      <div className="flex items-start justify-between">
        <p className="text-amber-200 font-semibold flex items-center gap-2"><AlertTriangle size={16} /> Save this device key now — it's shown only once.</p>
        <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={16} /></button>
      </div>
      <div className="grid gap-2 text-sm">
        <Row label="Device ID" value={reveal.id} on={() => copy(reveal.id, 'id')} done={copied === 'id'} />
        <Row label="Device key" value={reveal.key} on={() => copy(reveal.key, 'key')} done={copied === 'key'} mono />
      </div>
      <div>
        <p className="text-xs text-slate-400 mb-1">Send readings:</p>
        <pre className="bg-slate-950 border border-slate-700 rounded-lg p-3 text-[11px] text-slate-300 overflow-x-auto whitespace-pre-wrap">{curl}</pre>
      </div>
    </div>
  );
};
const Row: React.FC<{ label: string; value: string; on: () => void; done: boolean; mono?: boolean }> = ({ label, value, on, done, mono }) => (
  <div className="flex items-center gap-2">
    <span className="text-slate-400 w-20 shrink-0">{label}</span>
    <code className={`flex-1 bg-slate-950 rounded px-2 py-1 text-slate-200 truncate ${mono ? 'font-mono' : ''}`}>{value}</code>
    <button onClick={on} className="text-slate-400 hover:text-white">{done ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}</button>
  </div>
);

// ── Device detail: rollup chart + rotate + sell ──────────────────────────────
const DeviceDetail: React.FC<{ device: Device; onClose: () => void; onRotated: (k: string) => void }> = ({ device, onClose, onRotated }) => {
  const [rollups, setRollups] = useState<Rollup[]>([]);
  const [loading, setLoading] = useState(true);
  const [selling, setSelling] = useState(false);

  useEffect(() => { (async () => { try { setRollups(await getDeviceRollups(device.id, 30)); } finally { setLoading(false); } })(); }, [device.id]);

  const { data, metrics } = useMemo(() => {
    const byDay: Record<string, any> = {}; const m = new Set<string>();
    rollups.forEach((r) => { m.add(r.metric); byDay[r.day] = byDay[r.day] || { day: r.day }; byDay[r.day][r.metric] = r.avg; });
    return { data: Object.values(byDay).sort((a: any, b: any) => a.day.localeCompare(b.day)), metrics: [...m] };
  }, [rollups]);

  const rotate = async () => {
    if (!window.confirm('Rotate the key? The old key stops working immediately.')) return;
    try { onRotated(await rotateDeviceKey(device.id)); } catch (e: any) { alert(e.message); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-white flex items-center gap-2"><Activity size={18} className="text-indigo-400" /> {device.label || device.device_type}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={20} /></button>
        </div>

        {loading ? <Loader2 className="animate-spin text-indigo-400 mx-auto my-8" /> : data.length === 0 ? (
          <p className="text-slate-500 text-center py-8">No readings yet — send data to the ingest endpoint to see daily trends here.</p>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="day" stroke="#94a3b8" fontSize={11} />
              <YAxis stroke="#94a3b8" fontSize={11} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {metrics.map((m, i) => <Line key={m} type="monotone" dataKey={m} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} />)}
            </LineChart>
          </ResponsiveContainer>
        )}

        <div className="flex flex-wrap gap-2 mt-5">
          <button onClick={rotate} className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2"><RefreshCw size={14} /> Rotate key</button>
          <button onClick={() => setSelling((s) => !s)} className="bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"><Store size={14} /> Sell this data</button>
        </div>

        {selling && <SellForm device={device} metrics={metrics} onDone={() => setSelling(false)} />}
      </div>
    </div>
  );
};

const SellForm: React.FC<{ device: Device; metrics: string[]; onDone: () => void }> = ({ device, metrics, onDone }) => {
  const [title, setTitle] = useState(`${device.label || device.device_type} data`);
  const [desc, setDesc] = useState('');
  const [metric, setMetric] = useState('');
  const [price, setPrice] = useState(100);
  const [access, setAccess] = useState<'FREE' | 'PAID'>('PAID');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await createDataProduct({ device_id: device.id, title, description: desc, metric: metric || undefined, price_credits: access === 'PAID' ? price : 0, access });
      setDone(true); setTimeout(onDone, 1200);
    } catch (e: any) { alert(e.message || 'Could not list.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-4 bg-slate-800/60 border border-slate-700 rounded-xl p-4 space-y-3">
      <p className="text-sm text-white font-medium flex items-center gap-2"><Tag size={14} className="text-emerald-400" /> List on the data market</p>
      {device.sensitivity === 'HIGH' && <p className="text-xs text-amber-300">High-sensitivity data — buyers receive aggregated rollups only.</p>}
      <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" className="inp2" />
      <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Description (what's in the data, area, cadence)" className="inp2" />
      <div className="flex flex-wrap gap-2">
        <select value={metric} onChange={(e) => setMetric(e.target.value)} className="inp2 flex-1"><option value="">All metrics</option>{metrics.map((m) => <option key={m} value={m}>{m}</option>)}</select>
        <select value={access} onChange={(e) => setAccess(e.target.value as any)} className="inp2"><option value="PAID">Paid</option><option value="FREE">Free</option></select>
        {access === 'PAID' && <input type="number" min={0} value={price} onChange={(e) => setPrice(parseInt(e.target.value) || 0)} className="inp2 w-32" placeholder="credits" />}
      </div>
      <button onClick={submit} disabled={busy || done} className="bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 disabled:opacity-50">{busy ? <Loader2 size={14} className="animate-spin" /> : done ? <Check size={14} /> : <Store size={14} />} {done ? 'Listed!' : `List${access === 'PAID' ? ` for ${price} credits` : ' free'}`}</button>
      <style>{`.inp2{background:#0f172a;border:1px solid #475569;border-radius:8px;padding:8px 12px;color:#fff;font-size:13px;outline:none;width:100%}.inp2:focus{border-color:#6366f1}`}</style>
    </div>
  );
};

export default DevicesView;
